import {createStore, select, withProps} from '@ngneat/elf';
import {withRequestsCache, withRequestsStatus} from '@ngneat/elf-requests';
import {Injectable} from "@angular/core";
import {AnimalModel} from "../models/animal.model";
import {AnimalOriginModel} from "../models/animal-origin.model";
import {RestService} from "../../../shared/services/rest.service";
import {Observable, of} from 'rxjs';
import {tap, switchMap, catchError, take, map} from "rxjs/operators";
import {TranslocoService} from '@jsverse/transloco';
import {ToastService} from 'src/app/shared/services/toast.service';
import {AnimalMoveModel} from "../models/animal-move.model";

export interface AnimalState {
  animals: Record<number, AnimalModel>;
}

export const store = createStore(
  {name: 'animals'},
  withProps<AnimalState>({animals: {}}),
  withRequestsCache<'animals'>(),
  withRequestsStatus<'animals'>()
);

export interface AnimalSearchFilters {
  originsContact?: string;
  whereaboutsContact?: string;
  competentAuthority?: string;
  category?: string;
  type?: string;
  oldBookNumber?: string;
  petPassportNumber?: string;
  equinePassportNumber?: string;
  lifeNumber?: string;
  transponderNumber?: string;
  sex?: string;
  coatType?: string;
  coatColor?: string;
  name?: string;
  nameOld?: string;
  nameNew?: string;
  weightMin?: number;
  weightMax?: number;
  sizeMin?: number;
  sizeMax?: number;
  birthdayStart?: string; // ISO date YYYY-MM-DD
  birthdayEnd?: string;   // ISO date YYYY-MM-DD
  birthPlace?: string;
  dateOfReceiptStart?: string;
  dateOfReceiptEnd?: string;
  originType?: string;
  whereaboutType?: string;
  zipCode?: string;
  // Medizinisches: Teilstring-Suchen …
  /** Befund und Behandlung der Karteikarte. */
  medicalFinding?: string;
  /** Nachname bzw. Firma des Tierarztes an Karteikarte und Behandlung. */
  veterinarian?: string;
  /** Erreger der Behandlung. */
  treatmentType?: string;
  /** Präparatname der Behandlung. */
  treatmentPreparation?: string;
  /** Bezeichnung der Diagnose. */
  medicalCondition?: string;
  // … und Datumsfelder. Sie gehen unverändert ans Backend, das die Operator-Syntax selbst
  // versteht: `2026`, `2026-09`, `2026-09-03`, `>2026-09-01`, `<=2026-09-01`, `A..B`.
  /** Datum der Karteikarte. */
  medicalDate?: string;
  /** Nächste Fälligkeit einer Behandlung. */
  treatmentDueAt?: string;
  /** Ende der Quarantäne. */
  quarantineUntil?: string;
}

@Injectable({providedIn: 'root'})
export class AnimalRepository {
  private readonly loadedSearchTerms = new Set<string>();

  constructor(
    private readonly _restService: RestService,
    private readonly _toast: ToastService,
    private readonly _translateService: TranslocoService
  ) {
  }

  getAnimals$(): Observable<AnimalModel[]> {
    return store.pipe(
      select(state => {
        return Object.values(state.animals);
      })
    );
  }

  getById(animalId: number, cached: boolean = true): Observable<AnimalModel | undefined> {
    if (!animalId) return of(undefined);

    if (!cached) {
      return this._restService.httpGet(`/animals/${animalId}`).pipe(
        take(1),
        tap(response => {
          store.update(state => ({
            ...state,
            animals: {
              ...state.animals,
              [animalId]: {...response, hasFullData: true}
            }
          }));
        }),
        catchError(() => of(undefined))
      );
    }

    return store.pipe(
      select(state => state.animals[animalId]),
      switchMap(cachedAnimal => {
        if (cachedAnimal && cachedAnimal.hasFullData) {
          return of(cachedAnimal);
        }

        return this._restService.httpGet(`/animals/${animalId}`).pipe(
          take(1),
          tap(response => {
            store.update(state => ({
              ...state,
              animals: {
                ...state.animals,
                [animalId]: {...response, hasFullData: true}
              }
            }));
          }),
          catchError(() => of(undefined))
        );
      })
    );
  }

  getByShelter(shelterId: number): Observable<void> {
    return this._restService.httpGet(`/animals?short=true&pagination=false&animalShelter.id=${shelterId}`)
      .pipe(
        take(1),
        tap(data => {
          const updatedAnimals = data.reduce((acc: Record<number, AnimalModel>, animal: AnimalModel) => {
            acc[animal.id] = {...animal, hasFullData: false};
            return acc;
          }, {});
          store.update(state => ({
            ...state,
            animals: updatedAnimals
          }));
        }),
        map(() => void 0)
      );
  }

  searchAnimals(searchTerm: string, page: number = 1): Observable<AnimalModel[]> {
    return this._restService.httpGet(`/animals?page=${page}&search=${searchTerm}`).pipe(
      take(1),
      catchError(() => of([]))
    );
  }

  private buildAnimalFilterQuery(filters: AnimalSearchFilters = {}, searchTerm?: string, page: number = 1): string {
    const params: string[] = [`page=${encodeURIComponent(String(page))}`];

    if (searchTerm && searchTerm.trim().length > 0) {
      params.push(`search=${encodeURIComponent(searchTerm.trim())}`);
    }

    const pushIf = (key: string, value: any) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params.push(`${key}=${encodeURIComponent(String(value).trim())}`);
      }
    };

    // Partial or exact fields
    pushIf('originsContact', filters.originsContact);
    pushIf('whereaboutsContact', filters.whereaboutsContact);
    pushIf('competentAuthority', filters.competentAuthority);
    pushIf('category', filters.category);
    pushIf('type', filters.type);
    pushIf('oldBookNumber', filters.oldBookNumber);
    pushIf('petPassportNumber', filters.petPassportNumber);
    pushIf('equinePassportNumber', filters.equinePassportNumber);
    pushIf('lifeNumber', filters.lifeNumber);
    pushIf('transponderNumber', filters.transponderNumber);
    pushIf('sex', filters.sex);
    pushIf('coatType', filters.coatType);
    pushIf('coatColor', filters.coatColor);
    pushIf('name', filters.name);
    pushIf('birthPlace', filters.birthPlace);
    pushIf('originType', filters.originType);
    pushIf('whereaboutType', filters.whereaboutType);
    pushIf('zipCode', filters.zipCode);
    pushIf('medicalFinding', filters.medicalFinding);
    pushIf('veterinarian', filters.veterinarian);
    pushIf('treatmentType', filters.treatmentType);
    pushIf('treatmentPreparation', filters.treatmentPreparation);
    pushIf('medicalCondition', filters.medicalCondition);
    // Operator-Syntax unverändert durchreichen, das Backend wertet sie aus.
    pushIf('medicalDate', filters.medicalDate);
    pushIf('treatmentDueAt', filters.treatmentDueAt);
    pushIf('quarantineUntil', filters.quarantineUntil);

    // Numeric ranges: ApiPlatform common pattern: field=min..max or field
    const addRange = (field: string, min?: number, max?: number) => {
      const hasMin = typeof min === 'number' && !isNaN(min);
      const hasMax = typeof max === 'number' && !isNaN(max);
      if (hasMin && hasMax) {
        params.push(`${field}=${encodeURIComponent(`${min}..${max}`)}`);
      } else if (hasMin) {
        params.push(`${field}=${encodeURIComponent(`>${min}`)}`);
      } else if (hasMax) {
        params.push(`${field}=${encodeURIComponent(`<${max}`)}`);
      }
    };

    addRange('weight', filters.weightMin ? filters.weightMin * 1000 : undefined, filters.weightMax ? filters.weightMax * 1000 : undefined);
    addRange('size', filters.sizeMin, filters.sizeMax);

    // Date range (YYYY-MM-DD) similar between/gte/lte
    const addDateRange = (field: string, start?: string, end?: string) => {
      const hasStart = !!(start && start.trim());
      const hasEnd = !!(end && end.trim());
      if (hasStart && hasEnd) {
        params.push(`${field}=${encodeURIComponent(`${start}..${end}`)}`);
      } else if (hasStart) {
        params.push(`${field}=${encodeURIComponent(`>${start}`)}`);
      } else if (hasEnd) {
        params.push(`${field}=${encodeURIComponent(`<${end}`)}`);
      }
    };

    const addDate = (field: string, date?: string) => {
      if (date?.trim()) {
        params.push(`${field}=${encodeURIComponent(date)}`);
      }
    }

    addDateRange('birthday', filters.birthdayStart, filters.birthdayEnd);

    addDateRange('dateOfReceipt', filters.dateOfReceiptStart, filters.dateOfReceiptEnd);

    const query = `/animals?${params.join('&')}`;
    return query;
  }

  searchAnimalsWithFilters(filters: AnimalSearchFilters = {}, searchTerm?: string, page: number = 1): Observable<AnimalModel[]> {
    const url = this.buildAnimalFilterQuery(filters, searchTerm, page);
    return this._restService.httpGet(url).pipe(
      take(1),
      catchError(() => of([]))
    );
  }

  searchAnimalsAndUpdateStore(searchTerm: string, shelterId: number, page: number = 1): void {
    if (this.loadedSearchTerms.has(searchTerm)) return;
    this.loadedSearchTerms.add(searchTerm);

    this._restService.httpGet(`/animals?page=${page}&animalShelter.id=${shelterId}&search=${searchTerm}`)
      .pipe(take(1))
      .subscribe(data => {
        const updatedAnimals = data.reduce((acc: any, animal: any) => {
          acc[animal.id] = animal;
          return acc;
        }, {} as Record<number, AnimalModel>);

        store.update(state => ({
          ...state,
          animals: {...state.animals, ...updatedAnimals}
        }));
      });
  }

  addAnimal(animal: AnimalModel): void {
    store.update(state => {
      if (state.animals[animal.id]) {
        return state; // 🔹 Falls vorhanden, gibt es keine Änderung am State
      }

      return {
        ...state,
        animals: {
          ...state.animals,
          [animal.id]: animal
        }
      };
    });
  }

  create(payload: any): Observable<AnimalModel> {
    return this._restService.httpPost('/animals', payload).pipe(
      tap(response => store.update(state => ({
        ...state,
        animals: {...state.animals, [response.id]: response}
      }))),
      map(response => response)
    );
  }

  update(id: number, payload: Partial<AnimalModel>): Observable<AnimalModel> {
    return this._restService.httpPut(`/animals/${id}`, payload).pipe(
      tap(response => store.update(state => ({
        ...state,
        animals: {
          ...state.animals,
          [id]: {...response}
        }
      })))
    );
  }

  updateAnimalStore(id: number, changes: Partial<AnimalModel>): void {
    store.update(state => ({
      ...state,
      animals: {
        ...state.animals,
        [id]: {...state.animals[id], ...changes}
      }
    }));
  }

  addOrigin(animalId: number, newOrigin: AnimalOriginModel): void {
    this.getById(animalId).pipe(take(1)).subscribe(animal => {
      if (animal) {
        const updatedOrigins = [...(animal.origins || []), newOrigin];

        this.update(animalId, {origins: updatedOrigins}).subscribe({
          error: err => console.error('Error adding origin:', err)
        });
      } else {
        console.error(`Animal with ID ${animalId} not found`);
      }
    });
  }

  updateAnimalField(id: number, field: keyof AnimalModel, value: boolean): void {
    this._restService.httpPut(`/animals/${id}`, {[field]: value}).pipe(take(1)).subscribe(() => {
      store.update(state => ({
        ...state,
        animals: {...state.animals, [id]: {...state.animals[id], [field]: value}}
      }));
      this._toast.showSuccess(this._translateService.translate('animalEdit.toast.saveSuccess'));
    });
  }

  setSyncWithWebsite(id: number, checked: boolean): void {
    this.updateAnimalField(id, 'syncWithWebsite', checked);
  }

  setSanctuary(id: number, checked: boolean): void {
    this.updateAnimalField(id, 'sanctuary', checked);
  }

  setFosterHomeWanted(id: number, checked: boolean): void {
    this.updateAnimalField(id, 'fosterHomeWanted', checked);
  }

  setSponsored(id: number, checked: boolean): void {
    this.updateAnimalField(id, 'sponsored', checked);
  }

  setOutdoor(id: number, checked: boolean): void {
    this.updateAnimalField(id, 'outdoor', checked);
  }

  changeShelter(param: AnimalMoveModel) {
    return this._restService.httpPost(`/animals/${param.animalId}/change-shelter/${param.newShelterId}`, {
      newShelterChangeDate: param.newShelterChangeDate
    }).pipe(
      tap(() => {
        this._toast.showSuccess(this._translateService.translate('animalEdit.toast.shelterChangedSuccess'));
      }),
      catchError(err => {
        this._toast.showError(this._translateService.translate('animalEdit.toast.shelterChangedError'));
        console.error('Error changing shelter:', err);
        return of(undefined);
      })
    );
  }
}
